import React from "react";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import { Box, Typography } from "@mui/material";
import { useScreenshotTranslation } from "../../../lib/screenshotI18n";
import {
    ctaButtonSx,
    panelCardLightSx,
    panelCardSx,
    parchmentShellSx,
    phoneDeviceFrameSx,
    phoneDeviceScreenSx,
    utilityBarLightSx,
    utilityBarSx,
    utilityItemDarkTextSx,
    utilityItemSx,
    vaultRibbonSx,
} from "../styles/screenshotStyles";

const JM = "mobileScreenshot.jigsaw";

const JOURNEY_STOPS = [
    { x: 18, y: 72, icon: "⚽", key: "journey-theme-sports" },
    { x: 38, y: 48, icon: "🏰", key: "journey-theme-fantasy" },
    { x: 58, y: 62, icon: "🎨", key: "journey-theme-art" },
    { x: 72, y: 36, icon: "🚢", key: "journey-theme-ocean", current: true },
    { x: 86, y: 58, icon: "🌿", key: "journey-theme-nature" },
];

function UtilityItem({ Icon, label, light = false }) {
    return (
        <Box sx={light ? utilityItemDarkTextSx : utilityItemSx}>
            <Icon sx={{ fontSize: 26 }} aria-hidden />
            <Typography
                component="span"
                sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    textAlign: "center",
                    lineHeight: 1.2,
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

/** 02 — Sort / Zoom / piece count utility bar. */
export function SortingUtilityBar({ pieceCount = "45" }) {
    const { t } = useScreenshotTranslation();
    return (
        <Box sx={utilityBarLightSx}>
            <UtilityItem Icon={ColorLensOutlinedIcon} label={t(`${JM}.tool-sort-color`)} light />
            <UtilityItem Icon={ZoomInOutlinedIcon} label={t(`${JM}.tool-zoom`)} light />
            <UtilityItem
                Icon={ViewAgendaOutlinedIcon}
                label={t(`${JM}.meta-pieces`, { count: pieceCount })}
                light
            />
        </Box>
    );
}

/** 07 — Edge / Ghost / Magnifier tools. */
export function ToolsUtilityBar() {
    const { t } = useScreenshotTranslation();
    return (
        <Box sx={utilityBarSx}>
            <UtilityItem Icon={ExtensionOutlinedIcon} label={t(`${JM}.tool-isolate-edges`)} />
            <UtilityItem Icon={GridOnOutlinedIcon} label={t(`${JM}.tool-ghost-outline`)} />
            <UtilityItem Icon={ZoomInOutlinedIcon} label={t(`${JM}.tool-zoom-magnifier`)} />
        </Box>
    );
}

/** 05 — Daily countdown + rank + CTA. */
export function DailyLiveOpsPanel({ compact = false }) {
    const { t } = useScreenshotTranslation();
    return (
        <Box
            sx={{
                ...panelCardSx,
                ...(compact
                    ? {
                          mt: 0.5,
                          mb: "max(env(safe-area-inset-bottom, 0px), 8px)",
                          mx: 1.25,
                          p: 1.25,
                      }
                    : {}),
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: compact ? 0.4 : 0.75 }}>
                <TimerOutlinedIcon sx={{ fontSize: compact ? 18 : 22, color: "#e9d5ff" }} aria-hidden />
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: compact ? "0.78rem" : "0.88rem",
                        color: "#e9d5ff",
                    }}
                >
                    {t(`${JM}.daily-countdown`, { time: "05:30:15" })}
                </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: compact ? 0.25 : 0.5 }}>
                <LeaderboardOutlinedIcon
                    sx={{ fontSize: compact ? 17 : 20, color: "#c4b5fd" }}
                    aria-hidden
                />
                <Typography
                    sx={{
                        fontSize: compact ? "0.72rem" : "0.82rem",
                        fontWeight: 600,
                        color: "rgba(237,233,254,0.9)",
                    }}
                >
                    {t(`${JM}.daily-rank`, { rank: "12" })}
                </Typography>
            </Box>
            <Box sx={{ ...ctaButtonSx, ...(compact ? { mt: 0.75, py: 0.9 } : {}) }}>
                {t(`${JM}.daily-cta`)}
            </Box>
        </Box>
    );
}

/** 08 — Victory badge overlay + gallery CTA. */
export function VictoryCompletionOverlay() {
    const { t } = useScreenshotTranslation();
    return (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                background:
                    "radial-gradient(circle at 50% 45%, rgba(253,230,138,0.28) 0%, transparent 55%)",
            }}
        >
            <Box
                sx={{
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(160deg, #fef3c7 0%, #f59e0b 45%, #b45309 100%)",
                    border: "2px solid rgba(254,243,199,0.85)",
                    boxShadow:
                        "0 12px 32px rgba(180,83,9,0.4), 0 0 24px rgba(252,211,77,0.45)",
                    textAlign: "center",
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 900,
                        fontSize: "1.35rem",
                        letterSpacing: "0.04em",
                        color: "#78350f",
                        textShadow: "0 1px 0 rgba(254,243,199,0.7)",
                    }}
                >
                    {t(`${JM}.victory-badge`)}
                </Typography>
            </Box>
        </Box>
    );
}

export function VictoryCtaPanel() {
    const { t } = useScreenshotTranslation();
    return (
        <Box sx={panelCardLightSx}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", textAlign: "center", mb: 0.25 }}>
                {t(`${JM}.victory-vault`)}
            </Typography>
            <Box sx={ctaButtonSx}>{t(`${JM}.victory-cta`)}</Box>
        </Box>
    );
}

/** 06 — category ribbon under gallery. */
export function VaultCategoryRibbon() {
    const { t } = useScreenshotTranslation();
    return <Box sx={vaultRibbonSx}>{t(`${JM}.vault-ribbon`)}</Box>;
}

/** Soft particle dots for daily shell. */
export function DailyParticleField() {
    const dots = [
        { top: "12%", left: "18%", size: 4 },
        { top: "22%", left: "78%", size: 3 },
        { top: "38%", left: "12%", size: 5 },
        { top: "48%", left: "88%", size: 3 },
        { top: "68%", left: "22%", size: 4 },
        { top: "78%", left: "70%", size: 5 },
        { top: "16%", left: "48%", size: 2 },
        { top: "58%", left: "52%", size: 3 },
    ];
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                zIndex: 0,
                overflow: "hidden",
            }}
        >
            {dots.map((d, i) => (
                <Box
                    key={i}
                    sx={{
                        position: "absolute",
                        top: d.top,
                        left: d.left,
                        width: d.size,
                        height: d.size,
                        borderRadius: "50%",
                        bgcolor: "rgba(233,213,255,0.55)",
                        boxShadow: "0 0 10px rgba(192,132,252,0.55)",
                    }}
                />
            ))}
        </Box>
    );
}

/** 04 — illustrated journey map inside a phone frame. */
export function JourneyMapLayout({ headline, subtitle, header, compact = false }) {
    const { t } = useScreenshotTranslation();

    return (
        <Box
            sx={{
                ...parchmentShellSx,
                position: "relative",
                ...(compact
                    ? {
                          pt: "max(env(safe-area-inset-top, 0px), 8px)",
                          pb: "max(env(safe-area-inset-bottom, 0px), 8px)",
                      }
                    : {}),
            }}
        >
            {header}
            <Box
                sx={{
                    flex: compact ? "0 0 auto" : 1,
                    minHeight: 0,
                    display: "flex",
                    alignItems: compact ? "stretch" : "center",
                    justifyContent: compact ? "flex-start" : "center",
                    px: compact ? 0 : { xs: 1, sm: 1.5 },
                    py: compact ? 0 : { xs: 0.75, sm: 1 },
                    width: compact ? "72%" : "100%",
                    mx: compact ? "auto" : undefined,
                    boxSizing: "border-box",
                    mt: compact ? 0 : undefined,
                }}
            >
                <Box sx={{ ...phoneDeviceFrameSx, maxWidth: "100%", width: "100%" }}>
                    <Box
                        sx={{
                            ...phoneDeviceScreenSx,
                            bgcolor: "#e8d5b5",
                            backgroundImage: `
                                radial-gradient(circle at 20% 30%, rgba(120,53,15,0.08), transparent 40%),
                                radial-gradient(circle at 80% 70%, rgba(146,64,14,0.1), transparent 42%),
                                linear-gradient(165deg, #f5e6c8 0%, #e6d3a8 45%, #d4bc8a 100%)
                            `,
                            minHeight: 0,
                            aspectRatio: "1 / 1",
                            width: "100%",
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: 14,
                                left: "50%",
                                transform: "translateX(-50%)",
                                zIndex: 2,
                                px: 1.5,
                                py: 0.55,
                                borderRadius: 1.5,
                                bgcolor: "rgba(254,243,199,0.92)",
                                border: "1px solid rgba(120,53,15,0.35)",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            <MapOutlinedIcon sx={{ fontSize: 16, color: "#78350f" }} />
                            <Typography sx={{ fontWeight: 800, fontSize: 11.5, color: "#78350f" }}>
                                {headline}
                            </Typography>
                        </Box>

                        <Box
                            component="svg"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                            sx={{
                                position: "absolute",
                                inset: "18% 8% 22% 8%",
                                width: "84%",
                                height: "60%",
                                opacity: 0.55,
                            }}
                            aria-hidden
                        >
                            <path
                                d="M12 78 C 22 70, 28 55, 38 50 S 52 58, 58 62 S 68 40, 74 36 S 84 50, 90 58"
                                fill="none"
                                stroke="#b45309"
                                strokeWidth="1.2"
                                strokeDasharray="2 2.5"
                            />
                        </Box>

                        {JOURNEY_STOPS.map((stop) => (
                            <Box
                                key={stop.key}
                                sx={{
                                    position: "absolute",
                                    left: `${stop.x}%`,
                                    top: `${stop.y}%`,
                                    transform: "translate(-50%, -50%)",
                                    width: stop.current ? 44 : 36,
                                    height: stop.current ? 44 : 36,
                                    borderRadius: "50%",
                                    bgcolor: stop.current ? "#fef3c7" : "rgba(255,255,255,0.92)",
                                    border: stop.current
                                        ? "2px solid #f59e0b"
                                        : "1px solid rgba(120,53,15,0.35)",
                                    boxShadow: stop.current
                                        ? "0 0 16px rgba(245,158,11,0.65)"
                                        : "0 4px 10px rgba(0,0,0,0.15)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: stop.current ? 20 : 16,
                                    zIndex: 2,
                                }}
                                title={t(`${JM}.${stop.key}`)}
                            >
                                {stop.icon}
                            </Box>
                        ))}

                        {JOURNEY_STOPS.find((s) => s.current) ? (
                            <ExploreOutlinedIcon
                                sx={{
                                    position: "absolute",
                                    left: "72%",
                                    top: "28%",
                                    transform: "translate(-50%, -50%)",
                                    fontSize: 28,
                                    color: "#f59e0b",
                                    filter: "drop-shadow(0 0 8px rgba(245,158,11,0.8))",
                                    zIndex: 3,
                                }}
                                aria-hidden
                            />
                        ) : null}
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    ...panelCardLightSx,
                    bgcolor: "rgba(254,243,199,0.95)",
                    ...(compact
                        ? {
                              mt: 0.5,
                              mb: "max(env(safe-area-inset-bottom, 0px), 8px)",
                              mx: 1.25,
                              p: 1.25,
                          }
                        : {}),
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: compact ? 0.4 : 0.75 }}>
                    <ExploreOutlinedIcon
                        sx={{ fontSize: compact ? 18 : 22, color: "#92400e" }}
                        aria-hidden
                    />
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: compact ? "0.78rem" : "0.88rem",
                            color: "#78350f",
                        }}
                    >
                        {t(`${JM}.journey-chapter`)}
                    </Typography>
                </Box>
                {subtitle ? (
                    <Typography
                        sx={{
                            fontSize: compact ? "0.7rem" : "0.78rem",
                            color: "#92400e",
                            mb: compact ? 0.35 : 0.5,
                        }}
                    >
                        {subtitle}
                    </Typography>
                ) : null}
                <Box
                    sx={{
                        ...ctaButtonSx,
                        bgcolor: "#b45309",
                        boxShadow: "0 6px 18px rgba(180,83,9,0.35)",
                        ...(compact ? { mt: 0.75, py: 0.9 } : {}),
                    }}
                >
                    {t(`${JM}.journey-cta`)}
                </Box>
            </Box>
        </Box>
    );
}
